import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import Avatar from "boring-avatars";

/**
 * Descope-style floating testimonial cards for the login BRAND panel. A single card fades in at a
 * position chosen so it NEVER overlaps the centered logo or the bottom metric pills, lingers a few
 * seconds, fades out, then another appears elsewhere. Decorative (aria-hidden) and self-contained:
 * avatars are generated by `boring-avatars` using a random ABSTRACT/COLORFUL variant per name
 * (deterministic inline SVG — NOT real people); quotes
 * are a static set about agentic identity / security / guardrails / governance. Honors
 * `prefers-reduced-motion` by showing one static card. No external assets, so it stays within CSP.
 *
 * Placement is GEOMETRIC, not guessed: once the card is laid out we measure its real rectangle and
 * the live rectangles of the logo (`.oa-login__brand-inner`) and pills (`.oa-login__metrics`), then
 * reject candidate spots until one clears both (plus a margin), falling back to the top-left corner.
 */

interface Quote {
  readonly name: string;
  readonly role: string;
  readonly text: string;
}

const QUOTES: readonly Quote[] = [
  { name: "Ada N.", role: "Platform Security", text: "Every agent gets a verifiable identity before it ever touches a tool." },
  { name: "Marco T.", role: "Head of Governance", text: "Agentic governance stopped being a spreadsheet and started being enforced." },
  { name: "Priya R.", role: "SRE Lead", text: "Guardrails that travel with the action, not bolted on after the fact." },
  { name: "Leon K.", role: "IAM Architect", text: "One identity source for humans and agents, finally the same audit trail." },
  { name: "Sofia M.", role: "Compliance", text: "Hold-to-account is real here: who, what agent, which policy, when." },
  { name: "Dev P.", role: "AI Engineering", text: "Agentic security that scales with the fleet, not against it." },
  { name: "Nadia F.", role: "CISO", text: "We can revoke an agent's authority in seconds, across the whole plane." },
  { name: "Omar H.", role: "Risk & Controls", text: "Agentic identity means least-privilege is enforced, not aspirational." },
];

/** A vivid palette fed to boring-avatars for colorful icons (navy/blue brand base + warm accents). */
const AVATAR_PALETTE = ["#1e4562", "#5f8aa5", "#7aa7c4", "#f0ab3d", "#e86a5c", "#8f6bd4", "#2bb5a0"];

/** The more ABSTRACT, COLORFUL boring-avatars variants — one is chosen per name (deterministic). */
const AVATAR_VARIANTS = ["marble", "bauhaus", "sunset", "abstract"] as const;
type AvatarVariant = (typeof AVATAR_VARIANTS)[number];

/** Deterministic variant from the name, so the same person always gets the same abstract style. */
function variantFor(name: string): AvatarVariant {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const idx = Math.abs(hash) % AVATAR_VARIANTS.length;
  return AVATAR_VARIANTS[idx] ?? "marble";
}

/** Gap (px) kept between a card and the logo / pills before a position is accepted. */
const MARGIN = 20;
/** Panel inset (px) a card is kept inside. */
const INSET = 24;

interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

function overlaps(a: Rect, b: Rect, margin: number): boolean {
  return (
    a.left < b.right + margin &&
    a.right > b.left - margin &&
    a.top < b.bottom + margin &&
    a.bottom > b.top - margin
  );
}

/** Pick a top-left (px, relative to the layer) for a card of `cw`×`ch` that clears the obstacles. */
function pickPosition(
  layerW: number,
  layerH: number,
  cw: number,
  ch: number,
  obstacles: readonly Rect[],
): { readonly left: number; readonly top: number } {
  const maxLeft = Math.max(INSET, layerW - cw - INSET);
  const maxTop = Math.max(INSET, layerH - ch - INSET);
  for (let attempt = 0; attempt < 60; attempt++) {
    const left = INSET + Math.random() * (maxLeft - INSET);
    const top = INSET + Math.random() * (maxTop - INSET);
    const cardRect: Rect = { left, top, right: left + cw, bottom: top + ch };
    const clear = obstacles.every((o) => !overlaps(cardRect, o, MARGIN));
    if (clear) return { left, top };
  }
  // Fallback: top-left corner (always the furthest from the centered logo + bottom pills).
  return { left: INSET, top: INSET };
}

interface Placed {
  readonly quote: Quote;
  readonly id: number;
}

function nextContent(index: number, id: number): Placed {
  const quote = QUOTES[index % QUOTES.length] ?? QUOTES[0]!;
  return { quote, id };
}

export function FloatingQuotes(): ReactNode {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const [current, setCurrent] = useState<Placed>(() => nextContent(0, 0));
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: INSET, top: INSET });

  // Measure the card + obstacles AFTER layout, then place it so it clears logo and pills.
  const reposition = useCallback(() => {
    const layer = layerRef.current;
    const card = cardRef.current;
    if (layer === null || card === null) return;
    const layerBox = layer.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();

    const brand = layer.parentElement;
    const obstacles: Rect[] = [];
    if (brand !== null) {
      for (const sel of [".oa-login__brand-inner", ".oa-login__metrics"]) {
        const el = brand.querySelector(sel);
        if (el !== null) {
          const b = el.getBoundingClientRect();
          obstacles.push({
            left: b.left - layerBox.left,
            top: b.top - layerBox.top,
            right: b.right - layerBox.left,
            bottom: b.bottom - layerBox.top,
          });
        }
      }
    }
    setPos(
      pickPosition(layerBox.width, layerBox.height, cardBox.width, cardBox.height, obstacles),
    );
  }, []);

  // Reposition whenever the content changes (new card), before it becomes visible.
  useLayoutEffect(() => {
    reposition();
    const raf = window.requestAnimationFrame(() => {
      reposition();
      setVisible(true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [current, reposition]);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setVisible(true);
      return;
    }

    let index = 0;
    let id = 0;
    let hideTimer = 0;
    let swapTimer = 0;

    function cycle(): void {
      hideTimer = window.setTimeout(() => {
        setVisible(false);
        swapTimer = window.setTimeout(() => {
          index += 1;
          id += 1;
          setCurrent(nextContent(index, id)); // triggers reposition + show via layout effect
          cycle();
        }, 700); // fade-out duration
      }, 4200); // visible dwell
    }

    cycle();
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(swapTimer);
    };
  }, []);

  return (
    <div className="oa-login__quotes" aria-hidden="true" ref={layerRef}>
      <figure
        key={current.id}
        ref={cardRef}
        className="oa-quote-card"
        data-visible={visible ? "true" : "false"}
        style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
      >
        <span className="oa-quote-card__avatar" aria-hidden="true">
          <Avatar
            name={current.quote.name}
            variant={variantFor(current.quote.name)}
            size={40}
            colors={AVATAR_PALETTE}
          />
        </span>
        <figcaption className="oa-quote-card__body">
          <p className="oa-quote-card__text">“{current.quote.text}”</p>
          <p className="oa-quote-card__meta">
            <span className="oa-quote-card__name">{current.quote.name}</span>
            <span className="oa-quote-card__role">{current.quote.role}</span>
          </p>
        </figcaption>
      </figure>
    </div>
  );
}
